(function (root, factory) {
  'use strict';

  if (typeof module === 'object' && module.exports) {
    module.exports = { createAudibleMediaLightboxHost: factory };
    return;
  }

  factory({ root: root, document: root.document });
}(typeof globalThis !== 'undefined' ? globalThis : this, function createAudibleMediaLightboxHost(options) {
  'use strict';

  options = options || {};
  var root = options.root || (typeof globalThis !== 'undefined' ? globalThis : this);
  var document = options.document || root.document;
  var participant = null;
  var active = null;
  var bfcacheAmbient = null;
  var pendingAmbient = null;
  var transactionVersion = 0;
  var destroyed = false;
  var warned = false;

  function report(error, scope) {
    if (warned && scope === 'boot') return;
    if (scope === 'boot') warned = true;
    try {
      if (root.console && typeof root.console.warn === 'function') {
        root.console.warn('[SA Audible Media Lightbox] ' + scope, error);
      }
    } catch (_) {
      // Diagnostics are never a control dependency.
    }
  }

  function attempt(operation, scope) {
    try {
      operation();
      return true;
    } catch (error) {
      report(error, scope);
      return false;
    }
  }

  function hasNativeDialog() {
    return Boolean(
      root.HTMLDialogElement
      && root.HTMLDialogElement.prototype
      && typeof root.HTMLDialogElement.prototype.showModal === 'function'
    );
  }

  function dependenciesAvailable() {
    return Boolean(
      document
      && typeof document.addEventListener === 'function'
      && hasNativeDialog()
      && root.SAMediaPlayback
      && typeof root.SAMediaPlayback.registerParticipant === 'function'
      && root.SAScrollLock
      && typeof root.SAScrollLock.lock === 'function'
      && typeof root.SAScrollLock.unlock === 'function'
    );
  }

  function canonicalYouTubeSource(anchor) {
    var parsed;
    try {
      parsed = new root.URL(anchor.href, document.baseURI);
    } catch (_) {
      return null;
    }

    var host = parsed.hostname.toLowerCase();
    if (parsed.protocol !== 'https:' || (host !== 'www.youtube.com' && host !== 'youtube.com')) return null;
    if (parsed.port || parsed.username || parsed.password || parsed.pathname !== '/watch') return null;

    var ids = typeof parsed.searchParams.getAll === 'function'
      ? parsed.searchParams.getAll('v')
      : [parsed.searchParams.get('v')];
    if (ids.length !== 1 || !/^[A-Za-z0-9_-]{6,64}$/.test(ids[0] || '')) return null;

    return {
      canonicalUrl: parsed.href,
      embedUrl: 'https://www.youtube.com/embed/' + encodeURIComponent(ids[0])
        + '?rel=0&modestbranding=1&autoplay=1'
    };
  }

  function nonEmptyAttribute(element, name) {
    var value = element && typeof element.getAttribute === 'function'
      ? element.getAttribute(name)
      : null;
    return typeof value === 'string' && value.trim() !== '';
  }

  function isUsableSubmitControl(control, form) {
    if (!control || !control.isConnected || typeof control.focus !== 'function') return false;
    if (control.disabled === true || control.hasAttribute('disabled') || control.hasAttribute('hidden')) return false;
    if (control.hidden === true || control.getAttribute('aria-hidden') === 'true' || control.tabIndex < 0) return false;
    if (control.form && control.form !== form) return false;

    var tag = control.tagName;
    var type = (control.getAttribute('type') || '').toLowerCase();
    if (tag === 'BUTTON') return type === '' || type === 'submit';
    if (tag === 'INPUT') return type === 'submit' || type === 'image';
    return false;
  }

  function uniqueCloseControl(form) {
    var controls = form.querySelectorAll('button, input');
    var closeControl = null;
    for (var index = 0; index < controls.length; index += 1) {
      if (!isUsableSubmitControl(controls[index], form)) continue;
      if (closeControl) return null;
      closeControl = controls[index];
    }
    return closeControl;
  }

  function validateTrigger(anchor) {
    if (!participant || destroyed || !anchor || anchor.tagName !== 'A' || !anchor.isConnected) return null;
    if (anchor.hasAttribute('download')) return null;
    var target = anchor.getAttribute('target');
    if (target && target.toLowerCase() !== '_self') return null;

    var source = canonicalYouTubeSource(anchor);
    if (!source) return null;

    var section = anchor.closest('.shopify-section');
    if (!section || !section.isConnected) return null;

    var dialogs = section.querySelectorAll('[data-sa-audible-media-dialog]');
    if (dialogs.length !== 1) return null;
    var dialog = dialogs[0];
    if (!dialog.isConnected || dialog.tagName !== 'DIALOG') return null;
    if (typeof dialog.showModal !== 'function' || typeof dialog.close !== 'function') return null;
    if (dialog.hasAttribute('id')) return null;

    var iframes = dialog.querySelectorAll('iframe');
    var forms = dialog.querySelectorAll('form[method="dialog"]');
    if (iframes.length !== 1 || forms.length !== 1) return null;
    var iframe = iframes[0];
    var form = forms[0];
    if (!iframe.isConnected || !nonEmptyAttribute(iframe, 'title')) return null;
    if ((form.getAttribute('method') || '').toLowerCase() !== 'dialog') return null;
    var closeControl = uniqueCloseControl(form);
    if (!closeControl) return null;
    if (!nonEmptyAttribute(dialog, 'aria-label') && !nonEmptyAttribute(dialog, 'aria-labelledby')) return null;

    var ambientNodes = section.querySelectorAll('[data-sa-audible-media-ambient]');
    if (ambientNodes.length > 1) return null;
    var ambient = ambientNodes.length ? ambientNodes[0] : null;
    if (ambient && (ambient.tagName !== 'VIDEO' || ambient.muted !== true)) return null;

    return {
      opener: anchor,
      section: section,
      dialog: dialog,
      iframe: iframe,
      form: form,
      closeControl: closeControl,
      ambient: ambient,
      canonicalUrl: source.canonicalUrl,
      embedUrl: source.embedUrl
    };
  }

  function isEligibleAmbient(ambient) {
    return Boolean(ambient && ambient.isConnected && ambient.muted === true);
  }

  function pauseAmbient(record) {
    if (!record.ambient) return;
    record.ambientWasPlaying = record.ambient.paused === false;
    if (record.ambientWasPlaying) record.ambient.pause();
  }

  function resumeAmbient(saved) {
    if (!saved || !saved.wasPlaying || !isEligibleAmbient(saved.element)) return;
    var ambient = saved.element;
    var resumeVersion = ++transactionVersion;
    pendingAmbient = {
      element: ambient,
      wasPlaying: true,
      version: resumeVersion
    };
    try {
      var result = ambient.play();
      if (result && typeof result.then === 'function') {
        result.then(function () {
          if (destroyed || resumeVersion !== transactionVersion || active) {
            attempt(function () { ambient.pause(); }, 'ambient-stale-pause');
          }
          if (pendingAmbient && pendingAmbient.version === resumeVersion) pendingAmbient = null;
        }).catch(function () {
          // Browser autoplay policy may reject restoration; the dialog remains closed.
          if (pendingAmbient && pendingAmbient.version === resumeVersion) pendingAmbient = null;
        });
      } else if (pendingAmbient && pendingAmbient.version === resumeVersion) {
        pendingAmbient = null;
      }
    } catch (error) {
      if (pendingAmbient && pendingAmbient.version === resumeVersion) pendingAmbient = null;
      report(error, 'ambient-resume');
    }
  }

  function restoreFocus(opener) {
    if (!opener || !opener.isConnected || typeof opener.focus !== 'function') return;
    attempt(function () { opener.focus({ preventScroll: true }); }, 'focus-restore');
  }

  function focusDialog(record) {
    if (record.dialog.contains(document.activeElement)) return;
    attempt(function () { record.closeControl.focus({ preventScroll: true }); }, 'focus-dialog');
  }

  function deactivate(reason, expectedRecord) {
    var record = active;
    if (!record || (expectedRecord && expectedRecord !== record) || record.closing) return null;
    record.closing = true;
    ++transactionVersion;

    attempt(function () { record.iframe.removeAttribute('src'); }, 'iframe-clear');
    if (root.SAModalMotion && typeof root.SAModalMotion.close === 'function') {
      attempt(function () { root.SAModalMotion.close(record.dialog); }, 'modal-motion-close');
    }
    if (record.dialog.open) attempt(function () { record.dialog.close(); }, 'dialog-close');
    attempt(function () { participant.release(); }, 'participant-release');
    if (record.lockHeld) {
      record.lockHeld = false;
      attempt(function () { root.SAScrollLock.unlock(); }, 'scroll-unlock');
    }
    attempt(function () { record.dialog.removeEventListener('close', record.onClose); }, 'close-listener-remove');

    active = null;
    var savedAmbient = {
      element: record.ambient,
      wasPlaying: record.ambientWasPlaying
    };

    if (reason === 'customer' || reason === 'participant' || reason === 'rollback' || reason === 'replacement') {
      resumeAmbient(savedAmbient);
    }
    if (reason === 'customer') restoreFocus(record.opener);
    return savedAmbient;
  }

  function followFallback(url) {
    attempt(function () { root.location.assign(url); }, 'fallback-navigation');
  }

  function open(validated, event) {
    event.preventDefault();
    var inheritedAmbient = deactivate('replacement');
    var record = {
      version: ++transactionVersion,
      opener: validated.opener,
      section: validated.section,
      dialog: validated.dialog,
      iframe: validated.iframe,
      form: validated.form,
      closeControl: validated.closeControl,
      ambient: validated.ambient,
      ambientWasPlaying: false,
      lockHeld: false,
      closing: false,
      onClose: null
    };
    if (inheritedAmbient && inheritedAmbient.element === record.ambient && inheritedAmbient.wasPlaying) {
      record.ambientWasPlaying = true;
    }
    if (pendingAmbient && pendingAmbient.element === record.ambient && pendingAmbient.wasPlaying) {
      record.ambientWasPlaying = true;
      pendingAmbient = null;
    }
    record.onClose = function () { deactivate('customer', record); };
    active = record;

    try {
      if (!record.ambientWasPlaying) pauseAmbient(record);
      else if (record.ambient && record.ambient.paused === false) record.ambient.pause();
      record.dialog.addEventListener('close', record.onClose);
      record.dialog.showModal();
      if (root.SAModalMotion && typeof root.SAModalMotion.open === 'function') {
        root.SAModalMotion.open(record.dialog);
      }
      root.SAScrollLock.lock();
      record.lockHeld = true;
      participant.claim();
      record.iframe.setAttribute('src', validated.embedUrl);
      focusDialog(record);
    } catch (error) {
      report(error, 'open');
      deactivate('rollback', record);
      followFallback(validated.canonicalUrl);
    }
  }

  function eligibleClick(event) {
    return !event.defaultPrevented
      && event.button === 0
      && !event.metaKey
      && !event.ctrlKey
      && !event.shiftKey
      && !event.altKey;
  }

  function onClick(event) {
    if (destroyed || event.defaultPrevented) return;
    if (active && event.target === active.dialog && eligibleClick(event)) {
      deactivate('customer', active);
      return;
    }
    if (!eligibleClick(event) || !event.target || typeof event.target.closest !== 'function') return;
    var anchor = event.target.closest('a[data-sa-audible-media-trigger]');
    if (!anchor) return;
    var validated = validateTrigger(anchor);
    if (!validated) return;
    open(validated, event);
  }

  function onSectionUnload(event) {
    if (!active) return;
    var section = event.target;
    if (section === active.section) {
      deactivate('section', active);
      return;
    }
    var sectionId = event.detail && event.detail.sectionId;
    if (sectionId && active.section.id === 'shopify-section-' + sectionId) deactivate('section', active);
  }

  function removeHostListeners() {
    attempt(function () { document.removeEventListener('click', onClick); }, 'click-listener-remove');
    attempt(function () { document.removeEventListener('shopify:section:unload', onSectionUnload); }, 'section-listener-remove');
    attempt(function () { root.removeEventListener('pagehide', onPageHide); }, 'pagehide-listener-remove');
    attempt(function () { root.removeEventListener('pageshow', onPageShow); }, 'pageshow-listener-remove');
  }

  function destroy() {
    if (destroyed) return;
    deactivate('destroy');
    destroyed = true;
    ++transactionVersion;
    bfcacheAmbient = null;
    if (pendingAmbient) attempt(function () { pendingAmbient.element.pause(); }, 'ambient-destroy-pause');
    pendingAmbient = null;
    removeHostListeners();
    if (participant) attempt(function () { participant.destroy(); }, 'participant-destroy');
    participant = null;
  }

  function onPageHide(event) {
    if (!active && pendingAmbient) {
      var pending = pendingAmbient;
      pendingAmbient = null;
      ++transactionVersion;
      attempt(function () { pending.element.pause(); }, 'ambient-pagehide-pause');
      if (event.persisted) bfcacheAmbient = pending;
    }
    if (event.persisted) {
      if (active) bfcacheAmbient = deactivate('pagehide');
      return;
    }
    destroy();
  }

  function onPageShow(event) {
    if (!event.persisted || destroyed) return;
    if (active) deactivate('pagehide', active);
    var saved = bfcacheAmbient;
    bfcacheAmbient = null;
    resumeAmbient(saved);
  }

  if (!dependenciesAvailable()) {
    report(new Error('Required native dialog, Media Playback, or Scroll Lock dependency is unavailable.'), 'boot');
    return Object.freeze({ destroy: function () {} });
  }

  try {
    participant = root.SAMediaPlayback.registerParticipant({
      id: 'sa-audible-media-lightbox',
      pause: function () { deactivate('participant'); }
    });
    if (!participant
      || typeof participant.claim !== 'function'
      || typeof participant.release !== 'function'
      || typeof participant.destroy !== 'function') {
      throw new Error('Media Playback returned an invalid participant.');
    }
  } catch (error) {
    report(error, 'boot');
    return Object.freeze({ destroy: function () {} });
  }

  document.addEventListener('click', onClick);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  root.addEventListener('pagehide', onPageHide);
  root.addEventListener('pageshow', onPageShow);

  return Object.freeze({ destroy: destroy });
}));
